import { Label, Checkbox } from '@hqo/ui-atoms';
import {
  Controller,
  Control,
  FieldError,
  FieldErrors,
  FieldPath,
  UseFormRegister,
  useWatch,
  UseFormSetValue,
  ControllerRenderProps,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { PhoneInput } from '@hqo/phone-input';
import { cn } from '@hqo/react-hooks';
import { CreateVisitFormV2Values, Visitor } from '../../../../types/form-data.types';
import { InputField } from './input-field';
import { TypeaheadInputField } from './typeahead-input-field';
import { TypeaheadPhoneField } from './typeahead-phone-field';
import { VisitorPhotoField } from './visitor-photo-field';
import type { Value as PhoneNumber } from 'react-phone-number-input';
import { VisitStepFieldsConfig } from '../../../../hooks/use-ui-metadata-field-config.hook';
import { NoteForVisitor } from '../note-for-visitor/note-for-visitor';
import { NoteForReception } from '../note-for-reception/note-for-reception';
import { useValidationMessage } from '../../../../hooks/use-validation-message.hook';
import { useOptInAutoClear } from '../../../../hooks/use-opt-in-auto-clear.hook';
import { useCallback, useMemo } from 'react';

const visitorPath = (visitorIndex: number, field: string): FieldPath<CreateVisitFormV2Values> =>
  `visitors.${visitorIndex}.${field}` as FieldPath<CreateVisitFormV2Values>;

/** Visit-level notification prefs (API: `email_opt_in` / `sms_opt_in` on the visit). */
export const VisitNotificationOptIns = ({
  control,
  fieldsConfig,
  canOptInEmail,
  canOptInSms,
  idPrefix,
  errorSms,
}: {
  control: Control<CreateVisitFormV2Values>;
  fieldsConfig: VisitStepFieldsConfig;
  canOptInEmail: boolean;
  canOptInSms: boolean;
  idPrefix: string;
  errorSms?: FieldError;
}): JSX.Element | null => {
  const { formatMessage } = useIntl();
  const formatValidationMessage = useValidationMessage();

  const handleCheckedChange = useCallback(
    (field: ControllerRenderProps<CreateVisitFormV2Values, 'email_opt_in' | 'sms_opt_in'>) => (checked: boolean) => {
      field.onChange(checked);
    },
    [],
  );

  const emailOptInId = `email-opt-in-${idPrefix}`;
  const smsOptInId = `sms-opt-in-${idPrefix}`;

  if (
    fieldsConfig.notificationSelection.hidden ||
    (fieldsConfig.notificationSelection.emailCheckbox.hidden && fieldsConfig.notificationSelection.smsCheckbox.hidden)
  ) {
    return null;
  }

  return (
    <div className="space-y-3">
      {!fieldsConfig.notificationSelection.emailCheckbox.hidden && (
        <Controller
          name="email_opt_in"
          control={control}
          // eslint-disable-next-line react/jsx-no-bind
          render={({ field }) => (
            <label
              htmlFor={emailOptInId}
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg border bg-background transition-colors',
                canOptInEmail ? 'cursor-pointer hover:bg-muted/20' : 'opacity-50 cursor-not-allowed',
              )}
            >
              <Checkbox
                id={emailOptInId}
                checked={field.value === true}
                disabled={!canOptInEmail}
                className="w-5 h-5"
                onCheckedChange={handleCheckedChange(field)}
                data-cy="email-opt-in-checkbox"
              />
              <Label
                htmlFor={emailOptInId}
                className={cn('text-sm font-medium', canOptInEmail ? 'cursor-pointer' : 'cursor-not-allowed')}
              >
                {formatMessage({
                  id: 'createVisitFormV2.visitStep.emailOptIn',
                  defaultMessage: 'Send email notification to visitor',
                })}
              </Label>
            </label>
          )}
        />
      )}
      {!fieldsConfig.notificationSelection.smsCheckbox.hidden && (
        <Controller
          name="sms_opt_in"
          control={control}
          // eslint-disable-next-line react/jsx-no-bind
          render={({ field }) => (
            <label
              htmlFor={smsOptInId}
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg border bg-background transition-colors',
                canOptInSms ? 'cursor-pointer hover:bg-muted/20' : 'opacity-50 cursor-not-allowed',
              )}
            >
              <Checkbox
                id={smsOptInId}
                checked={field.value === true}
                disabled={!canOptInSms}
                className="w-5 h-5"
                onCheckedChange={handleCheckedChange(field)}
                data-cy="sms-opt-in-checkbox"
              />
              <Label
                htmlFor={smsOptInId}
                className={cn('text-sm font-medium', canOptInSms ? 'cursor-pointer' : 'cursor-not-allowed')}
              >
                {formatMessage({
                  id: 'createVisitFormV2.visitStep.smsOptIn',
                  defaultMessage: 'Send SMS notification to visitor',
                })}
              </Label>
            </label>
          )}
        />
      )}
      {errorSms && <p className="text-sm text-destructive">{formatValidationMessage(errorSms.message)}</p>}
    </div>
  );
};

interface VisitLevelFieldsProps {
  control: Control<CreateVisitFormV2Values>;
  register: UseFormRegister<CreateVisitFormV2Values>;
  errors: FieldErrors<CreateVisitFormV2Values>;
  fieldsConfig: VisitStepFieldsConfig;
  setValue: UseFormSetValue<CreateVisitFormV2Values>;
}

/** Visit-level fields (summary + notes) shown once when multiple visitor blocks are stacked. */
export const VisitLevelFields = ({
  control,
  register,
  errors,
  fieldsConfig,
  setValue,
}: VisitLevelFieldsProps): JSX.Element => {
  const visitSummaryValue = useWatch({ control, name: 'visitSummary' });
  const visitorsWatch = useWatch({ control, name: 'visitors' });
  const visitors = useMemo(() => (visitorsWatch ?? []) as Visitor[], [visitorsWatch]);
  const canOptInEmail = useMemo(() => visitors.some((v) => Boolean(v?.email?.trim())), [visitors]);
  const canOptInSms = useMemo(() => visitors.some((v) => Boolean(v?.phone?.trim())), [visitors]);

  useOptInAutoClear(canOptInEmail, canOptInSms, setValue);

  return (
    <div className="space-y-4 p-4 border border-border rounded-lg">
      {!fieldsConfig.visitSummary.hidden && (
        <InputField
          id="visitSummary"
          name="visitSummary"
          register={register}
          error={errors.visitSummary}
          value={visitSummaryValue}
          labelKey="createVisitFormV2.visitStep.visitSummary"
          labelDefault="Visit summary"
          required={fieldsConfig.visitSummary.required}
          placeholderKey="createVisitFormV2.visitStep.placeholders.visitSummary"
          placeholderDefault="e.g. Quarterly business review"
          maxLength={100}
        />
      )}

      <NoteForVisitor config={fieldsConfig.checkInInstructions} />
      <NoteForReception config={fieldsConfig.noteForReception} />

      <VisitNotificationOptIns
        control={control}
        fieldsConfig={fieldsConfig}
        canOptInEmail={canOptInEmail}
        canOptInSms={canOptInSms}
        idPrefix="visit-level"
        errorSms={errors.sms_opt_in}
      />
    </div>
  );
};

interface OneVisitorFormProps {
  control: Control<CreateVisitFormV2Values>;
  register: UseFormRegister<CreateVisitFormV2Values>;
  errors: FieldErrors<CreateVisitFormV2Values>;
  fieldsConfig: VisitStepFieldsConfig;
  setValue: UseFormSetValue<CreateVisitFormV2Values>;
  visitorIndex?: number;
  visitorOnly?: boolean;
}

export const OneVisitorForm = ({
  control,
  register,
  errors,
  fieldsConfig,
  setValue,
  visitorIndex = 0,
  visitorOnly = false,
}: OneVisitorFormProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const formatValidationMessage = useValidationMessage();

  const p = useMemo(() => (field: string) => visitorPath(visitorIndex, field), [visitorIndex]);

  const visitorErrors = errors.visitors?.[visitorIndex];
  const visitSummaryValue = useWatch({ control, name: 'visitSummary' });
  const firstNameValue = useWatch({ control, name: p('firstName') }) as string | undefined;
  const lastNameValue = useWatch({ control, name: p('lastName') }) as string | undefined;
  const visitorEmail = useWatch({ control, name: p('email') }) as string | undefined;
  const visitorPhone = useWatch({ control, name: p('phone') }) as string | undefined;

  useOptInAutoClear(Boolean(visitorEmail?.trim()), Boolean(visitorPhone?.trim()), setValue, !visitorOnly);

  const phoneInputId = `phone-${visitorIndex}`;

  return (
    <div className="space-y-4 p-4 border border-border rounded-lg">
      {/* Photo and Name row */}
      <div className="flex items-start gap-4">
        {!fieldsConfig.visitorPhoto.hidden && (
          <div className="flex-shrink-0">
            <VisitorPhotoField
              control={control}
              setValue={setValue}
              visitorIndex={visitorIndex}
              config={fieldsConfig.visitorPhoto}
              error={visitorErrors?.photoUuid}
              firstName={firstNameValue}
              lastName={lastNameValue}
            />
          </div>
        )}

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <InputField
            id={`firstName-${visitorIndex}`}
            name={p('firstName')}
            register={register}
            error={visitorErrors?.firstName}
            labelKey="createVisitFormV2.visitStep.firstName"
            labelDefault="First name"
            required
            placeholderKey="createVisitFormV2.visitStep.placeholders.firstName"
            placeholderDefault="e.g. John"
          />

          <InputField
            id={`lastName-${visitorIndex}`}
            name={p('lastName')}
            register={register}
            error={visitorErrors?.lastName}
            labelKey="createVisitFormV2.visitStep.lastName"
            labelDefault="Last name"
            required
            placeholderKey="createVisitFormV2.visitStep.placeholders.lastName"
            placeholderDefault="e.g. Smith"
          />
        </div>
      </div>

      {!fieldsConfig.email.hidden &&
        (fieldsConfig.typeaheadEnabled ? (
          <TypeaheadInputField
            control={control}
            setValue={setValue}
            visitorIndex={visitorIndex}
            required={fieldsConfig.email.required}
            error={visitorErrors?.email}
          />
        ) : (
          <InputField
            id={`email-${visitorIndex}`}
            name={p('email')}
            register={register}
            error={visitorErrors?.email}
            labelKey="createVisitFormV2.visitStep.email"
            labelDefault="Visitor's email"
            type="email"
            required={fieldsConfig.email.required}
            placeholderKey="createVisitFormV2.visitStep.placeholders.email"
            placeholderDefault="Enter email address"
          />
        ))}

      {!fieldsConfig.phone.hidden &&
        (fieldsConfig.typeaheadEnabled ? (
          <TypeaheadPhoneField
            control={control}
            setValue={setValue}
            visitorIndex={visitorIndex}
            required={fieldsConfig.phone.required}
            error={visitorErrors?.phone}
          />
        ) : (
          <div className="space-y-2">
            <Label htmlFor={phoneInputId} className="text-sm font-medium text-foreground gap-1">
              {formatMessage({ id: 'createVisitFormV2.visitStep.phone', defaultMessage: 'Phone number' })}
              {fieldsConfig.phone.required && <span className="text-destructive">*</span>}
            </Label>
            <Controller
              name={p('phone')}
              control={control}
              // eslint-disable-next-line react/jsx-no-bind
              render={({ field }) => {
                const showPhoneError = Boolean(visitorErrors?.phone);
                return (
                  <>
                    <PhoneInput
                      id={phoneInputId}
                      type="tel"
                      {...field}
                      value={field.value as PhoneNumber}
                      placeholder={formatMessage({
                        id: 'createVisitFormV2.visitStep.placeholders.phone',
                        defaultMessage: 'e.g. +1234567890',
                      })}
                      className={cn(
                        'border border-transparent rounded-[12px]',
                        showPhoneError && 'border-system-pink border-[1px]',
                      )}
                      data-cy="phone"
                    />
                    {showPhoneError && (
                      <p className="text-sm text-destructive">
                        {formatValidationMessage(visitorErrors?.phone?.message)}
                      </p>
                    )}
                  </>
                );
              }}
            />
          </div>
        ))}

      {!fieldsConfig.companyName.hidden && (
        <InputField
          id={`companyName-${visitorIndex}`}
          name={p('companyName')}
          register={register}
          error={visitorErrors?.companyName}
          labelKey="createVisitFormV2.visitStep.affiliation"
          labelDefault="Affiliation / company name"
          required={fieldsConfig.companyName.required}
          placeholderKey="createVisitFormV2.visitStep.placeholders.affiliation"
          placeholderDefault="e.g. Acme Corp"
        />
      )}

      {!visitorOnly && (
        <VisitNotificationOptIns
          control={control}
          fieldsConfig={fieldsConfig}
          canOptInEmail={Boolean(visitorEmail?.trim())}
          canOptInSms={Boolean(visitorPhone?.trim())}
          idPrefix={String(visitorIndex)}
          errorSms={errors.sms_opt_in}
        />
      )}

      {!visitorOnly && (
        <>
          {!fieldsConfig.visitSummary.hidden && (
            <InputField
              id="visitSummary"
              name="visitSummary"
              register={register}
              error={errors.visitSummary}
              value={visitSummaryValue}
              labelKey="createVisitFormV2.visitStep.visitSummary"
              labelDefault="Visit summary"
              required={fieldsConfig.visitSummary.required}
              placeholderKey="createVisitFormV2.visitStep.placeholders.visitSummary"
              placeholderDefault="e.g. Quarterly business review"
              maxLength={100}
            />
          )}

          <NoteForVisitor config={fieldsConfig.checkInInstructions} />
          <NoteForReception config={fieldsConfig.noteForReception} />
        </>
      )}
    </div>
  );
};
